import { type Editor } from '@tiptap/core';
import { cellAround } from '@tiptap/pm/tables';
import { isEditorDomConnected } from '../selection/is-editor-dom-connected';

/**
 * Floating-UI reference for the table toolbar. Anchors to the cell box, not
 * the caret: column alignment writes `text-align` without changing selection
 * `from`/`to`, so a caret-anchored menu jumps with the glyph. The cell's
 * layout box does not.
 *
 * `cellAround($from)` is used for both a caret and a `CellSelection` —
 * `prosemirror-tables` seeds `from`/`to` with the head cell's text range, so
 * that bbox still jumps on align. Returns a live `getBoundingClientRect` so
 * the menu tracks scroll. Null when the selection is not in a table (TipTap's
 * default anchoring).
 */
export const getTableMenuAnchor = (
  editor: Editor
): { getBoundingClientRect: () => DOMRect } | null => {
  if (!isEditorDomConnected(editor)) return null;

  const $cell = cellAround(editor.state.selection.$from);
  if (!$cell) return null;

  const dom = editor.view.nodeDOM($cell.pos);
  if (!(dom instanceof Element)) return null;

  const cellEl = dom.closest('td, th') ?? dom;
  return { getBoundingClientRect: () => cellEl.getBoundingClientRect() };
};
